import {
  Avatar,
  Button,
  Stack,
  Text,
  UnstyledButton,
} from "@flpstudio/design-system";

import type { DocumentAuthorDto } from "@/api/openapi";
import { Skeleton } from "@/components/atoms/Loading/Skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useAvailableAdvisorySessionsByExpertList } from "@/hooks/use-booking";
import { useExpertFullProfileSearchParams } from "@/hooks/use-page-search-params";
import {
  Amplitude,
  DocumentSessionCalloutEvent,
  buildExpertProperties,
} from "@/third-party/amplitude";
import type { Author } from "types";

type Props = {
  expertList: DocumentAuthorDto[];
};

function handleEventSend(expert: DocumentAuthorDto, action: string) {
  Amplitude.track(DocumentSessionCalloutEvent.name, {
    action,
    // TODO: Update amplitude function to use OpenAPI types
    ...buildExpertProperties(expert as Author),
  });
}

function getExpertIdentifier(expert: DocumentAuthorDto): string {
  return expert.urlId || expert.id || "";
}

export function BookingWidget(props: Props) {
  const { user } = useAuth();
  const { setExpertIdentifier } = useExpertFullProfileSearchParams();

  // Exclude the signed-in user from the list of available experts
  const filteredExpertIds = props.expertList.reduce(
    (filteredIds: string[], expert) => {
      if (expert.id !== user?.id) {
        filteredIds.push(getExpertIdentifier(expert));
      }

      return filteredIds;
    },
    [],
  );

  const { data: advisorySessionsList, isLoading } =
    useAvailableAdvisorySessionsByExpertList({
      identifierList: filteredExpertIds,
    });

  const availableExperts = props.expertList.filter(
    (_, index) => advisorySessionsList[index],
  );

  if (isLoading) {
    return <Skeleton />;
  }

  if (availableExperts.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Text className="font-semibold text-xl/normal">Book a session</Text>
      <Text>
        Need tailored advice on any IT challenges in your business? Book a 1x1
        session today.
      </Text>
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {availableExperts.map((expert) => (
          <li key={expert.id} className="flex items-center gap-2">
            <UnstyledButton
              aria-label={`View ${expert.firstName} ${expert.lastName}'s profile`}
              className="flex items-center gap-2"
              onClick={() => {
                setExpertIdentifier(getExpertIdentifier(expert));
                handleEventSend(
                  expert,
                  DocumentSessionCalloutEvent.properties.clickExpertName.action,
                );
              }}
            >
              <Avatar src={expert.profileImage} />
              <span className="line-clamp-1 break-all font-medium">
                {expert.firstName} {expert.lastName}
              </span>
            </UnstyledButton>
            <Button
              aria-label={`Book a session with ${expert.firstName} ${expert.lastName}`}
              className="ml-auto shrink-0 px-2"
              onClick={() => {
                setExpertIdentifier(getExpertIdentifier(expert));
                handleEventSend(
                  expert,
                  DocumentSessionCalloutEvent.properties.clickBook.action,
                );
              }}
            >
              Book
            </Button>
          </li>
        ))}
      </ul>
    </Stack>
  );
}
